import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {useEffect, useLayoutEffect, useRef} from 'react';

import {RootState} from '../../../../../index';
import {usePrevious} from '../../../../../hooks/usePrevious';
import {AUTO_UPDATED_TIMESCALES, LIVE_UPDATE_INTERVAL} from '../constants';

import {WidgetState} from '../../../../../types';
import {WidgetActionParams, WidgetDataParams} from '../types';

export interface UseWidgetDataParams extends WidgetDataParams {
    widgetAction: (params: WidgetActionParams) => void;
    resetAction: () => void;
    getWidgetState: (state) => WidgetState;
}

const useWidgetData = ({
    widgetAction,
    resetAction,
    getWidgetState,
    ...params
}: UseWidgetDataParams): WidgetState => {
    const {startDate, endDate, timeScale, selectedGames} = params;

    const dispatch = useDispatch();
    const intervalRef = useRef(null);
    const prevParams = usePrevious<WidgetDataParams>(params);

    const {data, loading, error} = useSelector<RootState, WidgetState>(
        getWidgetState,
        shallowEqual
    );

    /*
     * @param silent - Don't toggle loading state
     * @param overwriteCache - Overwrite cached data
     * */
    const fetchData = (silent = false) => {
        const islands = selectedGames.map(({value}) => String(value));

        if (islands.length) {
            dispatch(
                widgetAction({
                    fromTs: startDate ? startDate.valueOf() : undefined,
                    toTs: endDate ? endDate.valueOf() : undefined,
                    timeScale,
                    islands,
                    silent
                })
            );
        }
    };

    useEffect(() => {
        if (prevParams) {
            dispatch(resetAction());
        }
    }, [selectedGames]);

    useLayoutEffect(() => {
        if (prevParams) {
            fetchData(false);

            // Refresh data automatically for certain timescales
            if (AUTO_UPDATED_TIMESCALES.includes(timeScale)) {
                intervalRef.current = setInterval(() => {
                    fetchData(true);
                }, LIVE_UPDATE_INTERVAL);

                return () => {
                    clearInterval(intervalRef.current);
                };
            }
        }
    }, [startDate, endDate, timeScale, selectedGames]);

    return {
        data,
        loading,
        error,
        reload: () => fetchData(false)
    };
};

export default useWidgetData;
